import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import axios from 'axios';
import moment from 'moment';
import { Alert } from 'antd';
import MairaFilters from './MairaFilters';
import MairaStats from './MairaStats';
import MairaCallVolume from './MairaCallVolume';
import MairaOutcomes from './MairaOutcomes';
import MairaSentiment from './MairaSentiment';
import MairaReportBuilder, {
  TRANSFER_DESTINATION_CATEGORY_FILTER_KEYS,
  CALLER_OBJECTIVE_FILTER_KEYS,
} from './MairaReportBuilder';
import { csrfToken, mairaFilterParams } from './helpers';
import useLatestRequest from './useLatestRequest';
import { captureAnalytics } from '../../actions/preload';

const emptyDateRange = () => ({ startDate: null, endDate: null });
const DATE_FORMAT = 'YYYY-MM-DD';
const MAX_DATE_RANGE_DAYS = 100;
const emptyFilters = () => ({
  dateRange: emptyDateRange(),
  selectedVoiceAiNumberIds: [],
  selectedDepartmentSlugs: [],
});

const apiDateRange = (dateRangePayload) => ({
  startDate: dateRangePayload?.start_date || null,
  endDate: dateRangePayload?.end_date || null,
});

const hasCompleteDateRange = (dateRange) => Boolean(dateRange.startDate && dateRange.endDate);

const dateRangeExceedsMax = (dateRange) => {
  if (!hasCompleteDateRange(dateRange)) return false;

  const startDate = moment(dateRange.startDate, DATE_FORMAT);
  const endDate = moment(dateRange.endDate, DATE_FORMAT);
  if (!startDate.isValid() || !endDate.isValid()) return false;

  return endDate.diff(startDate, 'days') + 1 > MAX_DATE_RANGE_DAYS;
};

const cloneFilters = (filters) => ({
  dateRange: { ...filters.dateRange },
  selectedVoiceAiNumberIds: [...filters.selectedVoiceAiNumberIds],
  selectedDepartmentSlugs: [...filters.selectedDepartmentSlugs],
});

// Transfer destination/objective labels are classifier-owned and only
// format-validated upstream (snake_case), not enum-validated, so clamp them to the
// known taxonomy before sending to the third-party analytics sink: an unrecognized
// label collapses to 'other' so a novel/free-form token can never leak. sentiment
// buckets and outcomes are already bounded, so they pass through.
const TAXONOMY_DESTINATIONS = new Set(TRANSFER_DESTINATION_CATEGORY_FILTER_KEYS);
const TAXONOMY_OBJECTIVES = new Set(CALLER_OBJECTIVE_FILTER_KEYS);
const clampLabel = (label, allowed) => (allowed.has(label) ? label : 'other');

// Which chart a drill-down came from, derived from the keys the click handlers
// set on the segment. Lets the single buildSegmentReport call emit one analytics
// event for all four drill-downs (sentiment / outcome / transfer destination /
// objective). Objective is checked before destination because an objective
// drill-down also carries a destination.
const drilldownDimension = (segment) => {
  if (segment.sentimentBucket != null) return { dimension: 'sentiment', value: segment.sentimentBucket };
  if (segment.outcome != null) return { dimension: 'outcome', value: segment.outcome };
  if (segment.selectedCallerObjectives?.length) {
    return { dimension: 'transfer_objective', value: clampLabel(segment.selectedCallerObjectives[0], TAXONOMY_OBJECTIVES) };
  }
  if (segment.selectedTransferDestinationCategories?.length) {
    return {
      dimension: 'transfer_destination',
      value: segment.selectedTransferDestinationCategories.map((category) => clampLabel(category, TAXONOMY_DESTINATIONS)).join('/'),
    };
  }
  return { dimension: 'unknown', value: null };
};

const MairaDashboard = ({ active, filtersContainerId }) => {
  const [loading, setLoading] = useState(false);
  const [analyticsData, setAnalyticsData] = useState({});
  const [error, setError] = useState(null);
  const [draftFilters, setDraftFilters] = useState(emptyFilters());
  const [appliedFilters, setAppliedFilters] = useState(emptyFilters());
  const [dateRangeResolved, setDateRangeResolved] = useState(false);
  const [filtersTarget, setFiltersTarget] = useState(null);
  const reportBuilderRef = useRef(null);
  const runLatestDashboardRequest = useLatestRequest();
  const transferBreakdownEnabled = analyticsData.transfer_breakdown === true;

  const fetchDashboard = (filters, options = {}) => {
    if (!active) return;

    const requestedFilters = cloneFilters(filters);
    const includeDateParams = options.includeDateParams ?? hasCompleteDateRange(requestedFilters.dateRange);

    setLoading(true);
    setError(null);
    runLatestDashboardRequest({
      request: () => axios({
        method: 'GET',
        url: '/api/v1/maira_analytics',
        params: mairaFilterParams(requestedFilters, { includeDates: includeDateParams }),
        headers: {
          'X-CSRF-Token': csrfToken(),
        },
      }),
      onResolve: (response) => {
        const resolvedFilters = {
          ...requestedFilters,
          dateRange: apiDateRange(response.data.date_range),
        };

        setAnalyticsData(response.data);
        setDraftFilters(resolvedFilters);
        setAppliedFilters(resolvedFilters);
        setDateRangeResolved(true);
      },
      onReject: (requestError) => {
        console.error(requestError);
        setError(requestError.response?.data?.error || 'Unable to load MAIRA analytics.');
      },
      onFinally: () => setLoading(false),
    });
  };

  useEffect(() => {
    const filters = dateRangeResolved ? appliedFilters : emptyFilters();
    fetchDashboard(filters, {
      includeDateParams: dateRangeResolved && hasCompleteDateRange(filters.dateRange),
    });
  }, [active]);

  useEffect(() => {
    if (!active) {
      setFiltersTarget(null);
      return;
    }

    setFiltersTarget(document.getElementById(filtersContainerId));
  }, [active, filtersContainerId]);

  const handleDateRangeChange = (nextDateRange) => {
    setDraftFilters((currentFilters) => ({
      ...currentFilters,
      dateRange: nextDateRange,
    }));
  };

  const applyFilters = () => {
    const filters = cloneFilters(draftFilters);

    if (dateRangeExceedsMax(filters.dateRange)) {
      setError(`Date range cannot exceed ${MAX_DATE_RANGE_DAYS} days`);
      return;
    }

    fetchDashboard(filters, {
      includeDateParams: hasCompleteDateRange(filters.dateRange),
    });
  };

  const clearFilters = () => {
    const filters = emptyFilters();

    setDraftFilters(filters);
    fetchDashboard(filters, {
      includeDateParams: false,
    });
  };

  // A chart click drills the report into exactly the clicked segment. It scopes to
  // the dashboard's applied filters plus that segment, resetting the sibling
  // dimension (sentiment vs outcome) so the report total still equals the clicked
  // bar/slice count. Charts only render once analyticsData has loaded, so the
  // applied filters read here are always the resolved ones the user is looking at.
  const buildSegmentReport = (segment) => {
    reportBuilderRef.current?.buildForSegment({
      dateRange: appliedFilters.dateRange,
      selectedVoiceAiNumberIds: appliedFilters.selectedVoiceAiNumberIds,
      selectedDepartmentSlugs: appliedFilters.selectedDepartmentSlugs,
      selectedTransferDestinationCategories: [],
      selectedCallerObjectives: [],
      sentimentBucket: null,
      outcome: null,
      ...segment,
    });
    // Analytics runs after the drill-down so a capture error can never block it.
    const { dimension, value } = drilldownDimension(segment);
    captureAnalytics('maira_chart_drilldown_clicked', { dimension, value });
  };

  // The catch-all destination row aggregates calls with destination 'other' and
  // those with no recorded destination ('unknown' -> NULL/blank on the backend),
  // so its drill-down must select both filter categories to match the row.
  const transferDestinationFilterValues = (destination) => (
    destination === '_other_unknown' ? ['other', 'unknown'] : [destination]
  );

  const handleSentimentBucketClick = (bucket) => buildSegmentReport({ sentimentBucket: bucket });
  const handleOutcomeClick = (outcome) => buildSegmentReport({ outcome });
  const handleTransferDestinationClick = (destination) => buildSegmentReport({
    selectedTransferDestinationCategories: transferDestinationFilterValues(destination),
  });
  const handleTransferObjectiveClick = (destination, objectiveKey) => buildSegmentReport({
    selectedTransferDestinationCategories: transferDestinationFilterValues(destination),
    selectedCallerObjectives: [objectiveKey],
  });

  const filters = (
    <MairaFilters
      dateRange={draftFilters.dateRange}
      setDateRange={handleDateRangeChange}
      selectedVoiceAiNumberIds={draftFilters.selectedVoiceAiNumberIds}
      setSelectedVoiceAiNumberIds={(ids) => setDraftFilters((currentFilters) => ({
        ...currentFilters,
        selectedVoiceAiNumberIds: ids,
      }))}
      selectedDepartmentSlugs={draftFilters.selectedDepartmentSlugs}
      setSelectedDepartmentSlugs={(slugs) => setDraftFilters((currentFilters) => ({
        ...currentFilters,
        selectedDepartmentSlugs: slugs,
      }))}
      filterOptions={analyticsData.filter_options}
      loading={loading}
      onApply={applyFilters}
      onClear={clearFilters}
    />
  );

  return (
    <div className="maira-dashboard">
      {filtersTarget ? createPortal(filters, filtersTarget) : filters}
      {error && (
        <Alert
          type="error"
          showIcon
          message={error}
        />
      )}
      <MairaStats analyticsData={analyticsData} loading={loading} />
      <div className="maira-dashboard-grid">
        <MairaCallVolume callVolume={analyticsData.call_volume} loading={loading} />
        <MairaSentiment
          sentiment={analyticsData.sentiment}
          loading={loading}
          onBucketClick={handleSentimentBucketClick}
        />
        <MairaOutcomes
          outcomes={analyticsData.outcomes}
          taxonomy={analyticsData.taxonomy}
          loading={loading}
          onOutcomeClick={handleOutcomeClick}
          onTransferDestinationClick={handleTransferDestinationClick}
          onTransferObjectiveClick={handleTransferObjectiveClick}
        />
      </div>
      <MairaReportBuilder
        ref={reportBuilderRef}
        dateRange={appliedFilters.dateRange}
        dateRangeResolved={dateRangeResolved}
        selectedVoiceAiNumberIds={appliedFilters.selectedVoiceAiNumberIds}
        selectedDepartmentSlugs={appliedFilters.selectedDepartmentSlugs}
        filterOptions={analyticsData.filter_options}
        transferBreakdownEnabled={transferBreakdownEnabled}
        taxonomy={analyticsData.taxonomy}
      />
    </div>
  );
};

MairaDashboard.propTypes = {
  active: PropTypes.bool,
  filtersContainerId: PropTypes.string,
};

MairaDashboard.defaultProps = {
  active: false,
  filtersContainerId: 'maira-analytics-filters',
};

export { emptyDateRange };
export default MairaDashboard;
